import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/utils/movie";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMenuOpen(false);
    setCatOpen(false);
    setMobileCatOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const navLink = (to: string, label: string) => (
    <Link
      to={to}
      className="text-sm font-medium text-foreground-700 transition-colors hover:text-accent-600 whitespace-nowrap"
    >
      {label}
    </Link>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-movie-2-line text-xl"></i>
          </span>
          <span className="font-heading text-lg font-bold tracking-tight text-foreground-950 whitespace-nowrap">
            免费<span className="text-accent-500">在线电影</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          {navLink("/", "首页")}
          <div className="relative" ref={catRef}>
            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className="flex items-center gap-1 text-sm font-medium text-foreground-700 transition-colors hover:text-accent-600 whitespace-nowrap"
            >
              分类
              <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? "rotate-180" : ""}`}></i>
            </button>
            {catOpen && (
              <div className="absolute left-0 top-full mt-3 w-[420px] animate-fade-in rounded-lg border border-background-200 bg-background-50 p-3 shadow-lg shadow-foreground-950/5">
                <div className="grid grid-cols-3 gap-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-accent-50 hover:text-accent-700"
                    >
                      <i className={`${cat.icon} text-base text-primary-500`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {navLink("/about", "关于我们")}
          {navLink("/contact", "联系我们")}
        </nav>

        <div className="flex items-center gap-2">
          <form onSubmit={submitSearch} className="hidden items-center md:flex">
            <div className="flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 py-1.5 focus-within:border-accent-400">
              <i className="ri-search-line text-sm text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电影..."
                className="w-32 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400 lg:w-40"
              />
            </div>
          </form>

          <button
            type="button"
            aria-label="菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700 md:hidden"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="animate-fade-in border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 md:hidden">
          <form onSubmit={submitSearch} className="mb-3 flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-3 py-2">
            <i className="ri-search-line text-sm text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索电影..."
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
          </form>
          <div className="flex flex-col">
            <Link to="/" className="rounded-md px-2 py-2.5 text-sm font-medium text-foreground-800">首页</Link>
            <button
              type="button"
              onClick={() => setMobileCatOpen((v) => !v)}
              className="flex items-center justify-between rounded-md px-2 py-2.5 text-sm font-medium text-foreground-800"
            >
              分类
              <i className={`ri-arrow-down-s-line text-lg transition-transform ${mobileCatOpen ? "rotate-180" : ""}`}></i>
            </button>
            {mobileCatOpen && (
              <div className="grid grid-cols-2 gap-1 pb-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground-600"
                  >
                    <i className={`${cat.icon} text-base text-primary-500`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            )}
            <Link to="/about" className="rounded-md px-2 py-2.5 text-sm font-medium text-foreground-800">关于我们</Link>
            <Link to="/contact" className="rounded-md px-2 py-2.5 text-sm font-medium text-foreground-800">联系我们</Link>
          </div>
        </div>
      )}
    </header>
  );
}